'use client';

import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { useTranslations } from 'next-intl';
import React, { useState } from 'react';

import { Link } from '@/i18n/navigation';
import { useAuth } from '@/lib/contexts/AuthContext';

import { CreateAlertForm } from '../forms/CreateAlertForm';
import type {
  ExtendedIndexUiState,
  InitialLabels,
} from '../forms/CreateAlertForm/AlertSearchInterface';
import { AutoTypeOeuvreIconsProvider } from '../forms/CreateAlertForm/AutoTypeOeuvreIconsProvider';
import { Alert } from '../forms/fields/Alert';
import { AbstractModalV2 } from './AbstractModalV2';

/**
 * CreateAlertModal Component
 *
 * Responsibility (SRP): Manage alert creation modal visibility and success state
 *
 * Principles Applied:
 * - Uses NiceModal for declarative modal management
 * - DRY: Reuses existing CreateAlertForm and AbstractModalV2
 * - Separation of Concerns: Modal logic separate from form logic
 *
 * Architecture:
 * - Uses NiceModal.create() to register the modal
 * - Receives searchState as a prop when opened
 * - Manages internal success state after alert creation
 *
 * Integration Flow:
 * 1. The search buttons (useOpenCreateAlertModal) call
 *    NiceModal.show(CreateAlertModal, { searchState, initialLabels })
 * 2. Modal receives searchState as prop
 * 3. Passes to CreateAlertForm for filter initialization
 * 4. Shows success message after creation
 *
 * @example
 * // Show modal from any component
 * NiceModal.show(CreateAlertModal, { searchState: indexUiState })
 *
 * // Show modal with pre-filled labels (e.g., from entity page)
 * NiceModal.show(CreateAlertModal, {
 *   searchState: { msl: { 'auteurss_du_livres.auteur.id': ['123'] } },
 *   initialLabels: { authors: { '123': 'Victor Hugo' } }
 * })
 */
export const CreateAlertModal = NiceModal.create<{
  searchState: ExtendedIndexUiState;
  initialLabels?: InitialLabels;
}>(({ searchState, initialLabels }) => {
  const { visible, ...modal } = useModal();
  const [success, setSuccess] = useState(false);
  const t = useTranslations('Pages.Livre.FicheProduit.ModalCreerAlerte');
  const user = useAuth();

  return (
    <AbstractModalV2
      className="max-w-4xl"
      visible={visible}
      onClose={() => {
        modal.hide();
        setSuccess(false);
      }}
    >
      <AutoTypeOeuvreIconsProvider>
        <h2 className="mb-4 text-center text-lg font-semibold">
          {t('titre_modal')}
        </h2>
        <div className="flex justify-center space-x-4">
          {success && (
            <div>
              <Alert status="success" message={t('Messages.alerte_creee')} />
              <div className="flex flex-row items-center justify-center gap-4">
                <button
                  className="eo-btn-primary-outline"
                  onClick={() => {
                    modal.hide();
                    setSuccess(false);
                  }}
                >
                  {t('Champs.label_bouton_fermer')}
                </button>
                {user && (
                  <Link
                    href="/compte/alertes"
                    className="eo-btn-primary"
                    onClick={() => {
                      modal.hide();
                      setSuccess(false);
                    }}
                  >
                    {t('Champs.label_bouton_gerer_mes_alertes')}
                  </Link>
                )}
              </div>
            </div>
          )}
          {!success && searchState && (
            <CreateAlertForm
              searchState={searchState}
              initialLabels={initialLabels}
              onSuccess={() => {
                setSuccess(true);
              }}
            />
          )}
        </div>
      </AutoTypeOeuvreIconsProvider>
    </AbstractModalV2>
  );
});
